import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  useGetPublicProfileQuery,
  useGetUserTradeStatsQuery,
} from 'store/services/profile/profileApi';
import { ProfileTab } from 'types/interfaces/profile';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { ContactUserModal } from 'components/ContactUserModal/ContactUserModal';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import ProfileCard from './components/ProfileCard/ProfileCard';
import StatsCard from './components/StatsCard/StatsCard';
import NewUserCard from './components/NewUserCard/NewUserCard';
import ProfileTabs from './components/ProfileTabs/ProfileTabs';
import TradeHistoryTab from './components/TradeHistoryTab/TradeHistoryTab';
import ProfileWantListTab from './components/ProfileWantListTab/ProfileWantListTab';
import ProfileHaveListTab from './components/ProfileHaveListTab/ProfileHaveListTab';
import { TradeSidebar, MobileTradeDrawer } from './components/TradeSidebar';
import { ProfileTradeSidebarProvider, useProfileTradeSidebar } from './context/ProfileTradeSidebarContext';
import './UserProfilePage.css';

// Get art_crop URL from avatar URL
const getArtCropUrl = (avatarUrl: string | null): string | null => {
  if (!avatarUrl) return null;

  // If it's already an art_crop URL, return as-is
  if (avatarUrl.includes('art_crop')) return avatarUrl;

  // Try to convert from other image formats to art_crop
  // Scryfall URL format: https://cards.scryfall.io/{size}/{...}
  if (avatarUrl.includes('cards.scryfall.io')) {
    // Replace size indicator with art_crop
    return avatarUrl
      .replace('/normal/', '/art_crop/')
      .replace('/small/', '/art_crop/')
      .replace('/large/', '/art_crop/')
      .replace('/png/', '/art_crop/')
      .replace('/border_crop/', '/art_crop/');
  }

  return avatarUrl;
};

// Inner component that uses the trade sidebar context
const UserProfilePageInner: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { setReceiver, isOpen: isSidebarOpen } = useProfileTradeSidebar();

  // Profile and stats data
  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useGetPublicProfileQuery(username || '', {
    skip: !username,
  });

  const {
    data: stats,
    isLoading: isStatsLoading,
  } = useGetUserTradeStatsQuery(username || '', {
    skip: !username,
  });

  // Check if viewing own profile
  const isOwnProfile = currentUser?.username?.toLowerCase() === username?.toLowerCase();

  // Contact modal state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Mobile menu state
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Tab state - determine default tab
  // Default to 'want' for other profiles since that's where the trade action is
  const [activeTab, setActiveTab] = useState<ProfileTab>('want');

  // Set default tab based on profile type and stats
  useEffect(() => {
    if (isOwnProfile) {
      // For own profile, show history if they have trades, otherwise want list
      if (stats && stats.completedTrades > 0) {
        setActiveTab('history');
      } else {
        setActiveTab('want');
      }
    } else {
      // For other profiles, always default to want list (where trade actions happen)
      setActiveTab('want');
    }
  }, [stats, isOwnProfile]);

  // Set receiver for trade sidebar when profile loads
  useEffect(() => {
    if (profile?.id && profile?.username) {
      setReceiver(profile.username, profile.id);
    }
  }, [profile?.id, profile?.username, setReceiver]);

  // Background image URL (art_crop from avatar)
  const backgroundUrl = useMemo(() => {
    return getArtCropUrl(profile?.avatarUrl || null);
  }, [profile?.avatarUrl]);

  // Handle sidebar navigation attempts (for unauthenticated users)
  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Handle contact click
  const handleContactClick = () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    setIsContactModalOpen(true);
  };

  // Handle send cards click
  const handleSendCardsClick = () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    if (username) {
      navigate(`/sendcards?search=${encodeURIComponent(username)}`);
    }
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Show error state
  if (profileError) {
    const is404 = profileError && typeof profileError === 'object' && 'status' in profileError && profileError.status === 404;
    return (
      <div className="user-profile-layout">
        <div className="user-profile-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/profile', Component: <></> }}
            isPublicMode={true}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />
          <div className="user-profile-main-area">
            <div className="user-profile-error">
              <div className="error-icon">{is404 ? '🔍' : '⚠️'}</div>
              <h1 className="error-title">{is404 ? 'User Not Found' : 'Error Loading Profile'}</h1>
              <p className="error-message">
                {is404
                  ? `The user "${username}" does not exist.`
                  : 'There was an error loading this profile. Please try again later.'}
              </p>
            </div>
          </div>
        </div>
        <Footer />
        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername={username || ''}
        />
      </div>
    );
  }

  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Home', url: 'https://trademagic.gg/' },
    { name: 'Profile', url: `https://trademagic.gg/profile/u/${username?.toLowerCase()}` },
  ];

  // Determine whether to show stats or new user card
  // Show stats card if there are ANY stats to display (completedTrades > 0)
  const showStatsCard = stats && stats.completedTrades > 0;

  return (
    <>
      <SEOHelmet
        title={profile ? `${profile.username}'s Profile - TradeMagic` : 'Profile - TradeMagic'}
        description={
          profile
            ? `View ${profile.username}'s trading profile on TradeMagic. See their trade history, want list, and trading stats.`
            : 'View trader profiles on TradeMagic.'
        }
        canonical={`https://trademagic.gg/profile/u/${username?.toLowerCase()}`}
      />
      <BreadcrumbList breadcrumbs={breadcrumbs} />

      <div className="user-profile-layout">
        {/* Mobile Menu Overlay - click to close */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="user-profile-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/profile', Component: <></> }}
            isPublicMode={true}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="user-profile-main-area">
            {/* Header */}
            <TopNavigationBar
              title="Profile"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            {/* Background */}
            {backgroundUrl && (
              <div
                className="user-profile-background"
                style={{ backgroundImage: `url(${backgroundUrl})` }}
              />
            )}

            <div className={`user-profile-content ${!isOwnProfile && currentUser && isSidebarOpen ? 'sidebar-open' : ''}`}>
              {/* Sidebar */}
              <aside className="user-profile-sidebar">
                <ProfileCard
                  profile={profile}
                  isLoading={isProfileLoading}
                  isOwnProfile={isOwnProfile}
                  showContactButton={profile?.showContactButtonOnProfile ?? false}
                  onContactClick={handleContactClick}
                  onSendCardsClick={handleSendCardsClick}
                />

                {/* Stats Card or New User Card */}
                {showStatsCard ? (
                  <StatsCard stats={stats} isLoading={isStatsLoading} />
                ) : (
                  <NewUserCard username={username || ''} />
                )}
              </aside>

              {/* Main Content */}
              <main className="user-profile-main">
                <div className="user-profile-main-card">
                  <ProfileTabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    showHaveList={isOwnProfile || stats?.hasPublicHaveList === true}
                  />

                  {activeTab === 'history' && username && (
                    <TradeHistoryTab username={username} />
                  )}

                  {activeTab === 'want' && username && (
                    <ProfileWantListTab username={username} isOwnProfile={isOwnProfile} />
                  )}

                  {activeTab === 'have' && username && (
                    <ProfileHaveListTab username={username} isOwnProfile={isOwnProfile} />
                  )}
                </div>
              </main>
            </div>
          </div>
        </div>

        {/* Contact Modal */}
        {profile && (
          <ContactUserModal
            isOpen={isContactModalOpen}
            onClose={() => setIsContactModalOpen(false)}
            receiverId={profile.id}
            receiverUsername={profile.username}
            receiverAvatar={profile.avatarUrl || undefined}
          />
        )}

        {/* Login Prompt Modal */}
        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername={username || ''}
        />

        {/* Trade Sidebar - only shown when not viewing own profile */}
        {!isOwnProfile && currentUser && (
          <>
            <TradeSidebar />
            <MobileTradeDrawer />
          </>
        )}

        <Footer />
      </div>
    </>
  );
};

// Wrapper component that provides the trade sidebar context
export const UserProfilePage: React.FC = () => {
  return (
    <ProfileTradeSidebarProvider>
      <UserProfilePageInner />
    </ProfileTradeSidebarProvider>
  );
};

export default UserProfilePage;
